import _ from 'lodash';
import moment from 'moment';

import { CoreEnums } from '@/utils/domain/constants';
import { isNilOrEmpty, isNullOrEmpty } from '@/utils/infrastructure';
import { Regexp } from '@/utils/infrastructure/utilities';
import { parseFormulaFields } from './field-configuration';
import CoreFormula from './formula';
import { safeEval } from './safeEval';

const logicalOperatorOr = 'or';
const logicalOperatorAnd = 'and';

const isNullOrUndefined = (value) => _.isNil(value);

function isValidDate(value) {
  return moment(value, moment.ISO_8601, true).isValid();
}

function isNumber(value) {
  return !isNaN(value) && !isNaN(parseFloat(value));
}

function extractPatternFieldNames(value) {
  const pattern = /{{Form\.[^.]*\.Field\.([^}]+)}}/g;
  const fieldNames = [];
  let match;

  while ((match = pattern.exec(value)) !== null) {
    fieldNames.push(match[1]);
  }

  return fieldNames;
}

function patternFieldNamesValue(value, form, cleanString = false) {
  return extractPatternFieldNames(value)
    .map((name) => form?.getFieldValue(name))
    .filter(Boolean)
    .map((o) => {
      const val = o?.value ? (Regexp.isUUID(o?.value) ? o.label : o.value) : o;
      return cleanString ? cleanAccentsNonAlphanumeric(val) : val;
    });
}

function cleanAccentsNonAlphanumeric(value) {
  if (isValidDate(value) || isNilOrEmpty(value) || _.isObject(value) || isNumber(value)) {
    if (isNumber(value)) return parseFloat(value);
    return value;
  }

  // Define a mapping object for Latin American accented characters
  const accentMap = {
    á: 'a',
    é: 'e',
    í: 'i',
    ó: 'o',
    ú: 'u',
    Á: 'A',
    É: 'E',
    Í: 'I',
    Ó: 'O',
    Ú: 'U',
    ñ: 'n',
    Ñ: 'N',
    ü: 'u',
    Ü: 'U',
  };

  const cleanedStr = value
    ?.toString()
    ?.replace(/[áéíóúÁÉÍÓÚñÑüÜ]/g, (match) => accentMap[match] || match);

  return cleanedStr?.replace(/[^a-zA-Z0-9 ]/g, '');
}

function handleFieldBehaviors(currentCase, conditionsPassed, behaviors) {
  if (conditionsPassed) {
    switch (currentCase.fieldBehavior) {
      // ReadOnly = 1
      case CoreEnums.FormFieldBehavior.ReadOnly:
        _.set(behaviors, 'disabled', true);
        _.set(behaviors, 'readOnly', true);
        break;

      // Hidden = 2
      case CoreEnums.FormFieldBehavior.Hidden:
        _.set(behaviors, 'hidden', true);
        _.set(behaviors, 'rules', [{ required: false }]);
        break;

      // Visible = 3
      case CoreEnums.FormFieldBehavior.Visible:
        _.set(behaviors, 'hidden', false);
        break;

      // Required = 4
      case CoreEnums.FormFieldBehavior.Required:
        _.set(behaviors, 'rules', [{ required: true }]);
        break;

      // NotRequired = 5
      case CoreEnums.FormFieldBehavior.NotRequired:
        _.set(behaviors, 'rules', [{ required: false }]);
        break;

      // VisibleAndSkipFormulaEvaluation = 6
      case CoreEnums.FormFieldBehavior.VisibleAndSkipFormulaEvaluation:
        _.set(behaviors, 'hidden', false);
        break;

      // HiddenAndSkipFormulaEvaluation = 7
      case CoreEnums.FormFieldBehavior.HiddenAndSkipFormulaEvaluation:
        _.set(behaviors, 'hidden', true);
        break;

      default:
        break;
    }
  } else {
    switch (currentCase.fieldBehavior) {
      // ReadOnly = 1
      case CoreEnums.FormFieldBehavior.ReadOnly:
        _.set(behaviors, 'disabled', false);
        _.set(behaviors, 'readOnly', false);
        // appearance: 'fill-read',
        break;

      // Hidden = 2
      case CoreEnums.FormFieldBehavior.Hidden:
        _.set(behaviors, 'hidden', false);
        break;

      // Visible = 3
      case CoreEnums.FormFieldBehavior.Visible:
        _.set(behaviors, 'hidden', true);
        break;

      // Required = 4
      case CoreEnums.FormFieldBehavior.Required:
        _.set(behaviors, 'rules', [{ required: false }]);
        break;

      // NotRequired = 5
      case CoreEnums.FormFieldBehavior.NotRequired:
        _.set(behaviors, 'rules', [{ required: !behaviors?.hidden }]);
        break;

      // VisibleAndSkipFormulaEvaluation = 6
      case CoreEnums.FormFieldBehavior.VisibleAndSkipFormulaEvaluation:
        _.set(behaviors, 'hidden', true);
        break;

      // HiddenAndSkipFormulaEvaluation = 7
      case CoreEnums.FormFieldBehavior.HiddenAndSkipFormulaEvaluation:
        _.set(behaviors, 'hidden', false);
        break;

      default:
        break;
    }
  }
}

function handleCommandBehaviors(currentCase, conditionsPassed, behaviors) {
  if (conditionsPassed) {
    switch (currentCase.commandBehavior) {
      case CoreEnums.FormTableCommandBehavior.Denied:
        _.set(behaviors, 'hidden', true);
        break;

      case CoreEnums.FormTableCommandBehavior.Allow:
        _.set(behaviors, 'hidden', false);
        break;

      default:
        break;
    }
  } else {
    switch (currentCase.commandBehavior) {
      case CoreEnums.FormFieldBehavior.Denied:
        _.set(behaviors, 'hidden', false);
        break;

      case CoreEnums.FormFieldBehavior.Allow:
        _.set(behaviors, 'hidden', true);
        break;

      default:
        break;
    }
  }
}

export default function evaluateFieldBehaviors(behaviorCases, tableInternalName) {
  if (!isNullOrUndefined(behaviorCases) && behaviorCases.length > 0) {
    const behaviors = {};

    for (const currentCase of behaviorCases) {
      let anyConditionFail = false;
      let anyConditionPass = false;
      const operator = currentCase.logicalOperator ?? 'or';

      if (currentCase.conditionGroups && currentCase.conditionGroups.length > 0) {
        for (const currentCaseGroup of currentCase.conditionGroups) {
          if (currentCaseGroup.conditions && currentCaseGroup.conditions.length > 0) {
            const successGroupEvaluation = evaluateGroup.apply(this, [currentCaseGroup]);

            if (CoreFormula.IsTrue(successGroupEvaluation)) {
              anyConditionPass = true;
              if (operator === logicalOperatorOr) break;
            } else {
              anyConditionFail = true;
              if (operator === logicalOperatorAnd) break;
            }
          }
        }
      }

      const conditionsPassed =
        !anyConditionFail || (operator == logicalOperatorOr && anyConditionPass);

      if (currentCase?.fieldBehavior) {
        handleFieldBehaviors(currentCase, conditionsPassed, behaviors);
      } else if (currentCase?.commandBehavior) {
        handleCommandBehaviors(currentCase, conditionsPassed, behaviors);
      }
    }

    return behaviors;
  }
}

function evaluateGroup(currentCaseGroup = {}) {
  let anyConditionFail = false;
  let anyConditionPass = false;

  const operator = currentCaseGroup.logicalOperator ?? 'or';

  for (const condition of currentCaseGroup.conditions) {
    const successCondition = evaluateCondition.apply(this, [condition]);

    if (CoreFormula.IsTrue(successCondition)) {
      anyConditionPass = true;
      if (operator === logicalOperatorOr) break;
    } else {
      anyConditionFail = true;
      if (operator === logicalOperatorAnd) break;
    }
  }

  return !anyConditionFail || (operator == logicalOperatorOr && anyConditionPass);
}

function evaluateCondition(behaviorCondition = {}) {
  let value = '';
  let form;

  switch (behaviorCondition.valueSource) {
    // FormField = 1
    case CoreEnums.MappingValueSource.FormField: {
      const [outForm, inForm] = this || [];
      form =
        behaviorCondition.parentTableFieldId ||
        behaviorCondition.sourceField?.parentTable ||
        !outForm
          ? inForm
          : outForm;

      const temp = form?.getFieldValue(behaviorCondition.fieldIdName);

      if (_.isObject(temp) && !moment.isMoment(temp) && !_.isArray(temp)) {
        value = Regexp.isUUID(temp.value) ? temp.label : temp.value;
      } else if (_.isArray(temp)) {
        value = temp
          .map((o) => (_.isObject(o) ? (Regexp.isUUID(o.value) ? o.label : o.value) : o))
          .join(',');
      } else {
        value = temp;
      }

      break;
    }

    // Formula = 3
    case CoreEnums.MappingValueSource.Formula: {
      let formula = behaviorCondition.formula;
      const formulaFields = parseFormulaFields(formula);

      _.each(formulaFields, (n) => {
        formula = _.replace(
          formula,
          new RegExp(_.toString(`{{${n}}}`), 'g'),
          `getInputFormulaValue(${_.split(n, '.').pop()})`,
        );
      });

      const getInputFormulaValue = CoreFormula.getInputFormulaValue;
      value = safeEval(formula, { getInputFormulaValue });
      break;
    }

    // Variable = 2
    // FixedValue = 4
    // InternalField = 5
    case CoreEnums.MappingValueSource.Variable:
    case CoreEnums.MappingValueSource.FixedValue:
    case CoreEnums.MappingValueSource.InternalField:
      value = behaviorCondition.value;
      break;

    default:
      break;
  }

  const cleanedValue = cleanAccentsNonAlphanumeric(value);
  const cleanedConditionValue = Regexp.isFormFieldPattern(behaviorCondition.value)
    ? patternFieldNamesValue(behaviorCondition.value, form, true).join(',')
    : cleanAccentsNonAlphanumeric(behaviorCondition.value);

  switch (behaviorCondition.comparisonOperator) {
    // Equal = 1
    case CoreEnums.ComparisonOperator.Equal:
      return cleanedValue === cleanedConditionValue;

    // NotEqual = 2
    case CoreEnums.ComparisonOperator.NotEqual:
      return cleanedValue !== cleanedConditionValue;

    // GreaterThan = 3
    case CoreEnums.ComparisonOperator.GreaterThan:
      return cleanedValue > cleanedConditionValue;

    // GreaterThanOrEqual = 4
    case CoreEnums.ComparisonOperator.GreaterThanOrEqual:
      return cleanedValue >= cleanedConditionValue;

    // LessThan = 5
    case CoreEnums.ComparisonOperator.LessThan:
      return cleanedValue < cleanedConditionValue;

    // LessThanOrEqual = 6
    case CoreEnums.ComparisonOperator.LessThanOrEqual:
      return cleanedValue <= cleanedConditionValue;

    // Contains = 7
    case CoreEnums.ComparisonOperator.Contains: {
      const conditionValue = Regexp.isFormFieldPattern(behaviorCondition.value)
        ? patternFieldNamesValue(behaviorCondition.value, form).join(',')
        : behaviorCondition.value;

      return value?.includes?.(conditionValue);
    }

    // NotContains = 8
    case CoreEnums.ComparisonOperator.NotContains: {
      const conditionValue = Regexp.isFormFieldPattern(behaviorCondition.value)
        ? patternFieldNamesValue(behaviorCondition.value, form).join(',')
        : behaviorCondition.value;

      return !value?.includes?.(conditionValue);
    }

    // IsNull = 9
    case CoreEnums.ComparisonOperator.IsNull:
      return isNullOrEmpty(value);

    // IsNotNull = 10
    case CoreEnums.ComparisonOperator.IsNotNull:
      return !isNullOrEmpty(value);
  }

  return false;
}
